import {useEffect, useState} from "react";
import { Dropdown, Avatar, Space, Typography, Button, Select } from 'antd';
import { QuestionCircleOutlined, GlobalOutlined, MenuOutlined } from '@ant-design/icons';
import { useParams, useLocation, matchRoutes, useNavigate } from "react-router-dom";
import Api from "../../services/api";
import { useAllApps } from "../../hooks/useData";
import routes from "../../routes";
// Using Ant Design Select for app picker to keep header compact
import { useAuth } from '../../contexts/AuthContext';

const { Text } = Typography;

function Header({ collapsed = false, onMobileMenuToggle, mobileMenuOpen = false, sidebarWidth = 256 }) { 
  const { userInfo } = useAuth();
  const auth = userInfo || null;
  const navigate = useNavigate();
  const location = useLocation();
  const { allApps } = useAllApps();
  const [data, setData] = useState([]);
  const { code, os } = useParams();
  const [selectedValue, setSelectedValue] = useState(null); // value string: `${code}-${os}`
  const { logout } = useAuth();

  const renderAvatar = (size = 32) => {
    if (auth?.avatar) {
      return <Avatar src={auth.avatar} size={size} />;
    }
    // Fallback to default Ant Design avatar if no avatar from Google
    return <Avatar src="https://gw.alipayobjects.com/zos/rmsportal/BiazfanxmamNRoxxVxka.png" size={size} />;
  };

  useEffect(() => {
    const mapped = allApps.map(i => ({
      value: `${i.code}-${i.os}`,
      label: `${i.code} - ${i.name} ${i.os}`,
      thumb: i.thumb || null,
      os: i.os || null,
    }))
    setData(mapped);
  }, [allApps]);

    useEffect(() => {
        if (data.length > 0 && code && os) {
            const matched = data.find(opt => opt.value === `${code}-${os}`);
            setSelectedValue(matched ? matched.value : null);
        }
    }, [data, code, os]);

  const handleLogout = () => {
    Api.post('/api/logout').then(() => {
      logout();
      window.dispatchEvent(new CustomEvent('logout'));
      navigate('/login');
    });
  }

    const handleChangeApp = (value) => {
        if (!value) return;

        setSelectedValue(value);
        const [code, os] = String(value).split('-');

        const matches = matchRoutes(routes.routes, location);
        const item = matches?.filter((item) => !item.route.children)[0];

        if (item?.route?.path) {
            const url = item.route.path
                .replace(':studio', auth.studio)
                .replace(':code', code)
                .replace(':os', os);
            navigate(url);
        }
    };

  const dropdownItems = [
    {
      key: 'profile',
      label: (
        <div style={{textAlign: 'center', padding: 15}}>
          <div style={{marginBottom: 15}}>
            {renderAvatar(26)}
          </div>
          <p style={{marginBottom: 0}} className="user__mail d-lg-inline-block">{auth?.email}</p>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'logout',
      label: <span>Sign out</span>,
      onClick: handleLogout,
    },
  ];

  return (
    <>
      <style>{`
        /* Mobile <= 992px */
        @media (max-width: 992px) {
          .mobile-menu-btn {
            display: inline-flex !important;
          }
          .brand-container { display: none !important; }
          .user-name { display: none; }
        }
        @media (min-width: 993px) {
          .mobile-menu-btn {
            display: none !important;
          }
        }
        .app-select .ant-select-selector { 
          height: 32px; 
          padding: 0 !important; 
          padding-right: 25px !important; 
        }
        .app-select .ant-select-selection-item { display: flex; align-items: center; gap: 10px; padding: 0 !important; }
        .app-select .ant-select-arrow { right: 6px; }
        /* Align app select with content on PC */
        @media (min-width: 993px) {
          .app-select-container {
            margin-left: 138px !important;
            flex: 0 0 auto !important;
          }
          /* When sidebar is collapsed, override margin-left */
          .app-select-container.is-collapsed { margin-left: 23px !important; }
          .app-select {
            width: auto !important;
          }
        }
        @media (max-width: 992px) {
          .header-mobile-row { display: flex !important; align-items: center !important; gap: 8px !important; width: 100% !important; }
          .header-mobile-row.no-app { justify-content: space-between !important; }
          .app-select-container {
            margin-left: 8px !important;
            margin-right: 8px !important;
            flex: 1 1 auto !important;
            min-width: 0 !important;
            width: 100% !important;
            max-width: none !important;
          }
          .desktop-avatar { display: none !important; }
          .mobile-avatar { display: block !important; }
          .mobile-menu-btn {
            flex-shrink: 0 !important;
            width: 32px !important;
            min-width: 32px !important;
            flex: 0 0 32px !important;
          }
          .brand-container {
            display: none !important;
          }
          .app-select { 
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }
          .app-select-option {
            max-width: 100%;
            overflow: hidden;
          }
          .app-select-label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
            min-width: 0;
          }
          .ant-select-dropdown {
            max-width: calc(100vw - 32px) !important;
          }
          .ant-select-item-option-content {
            max-width: 100%;
            overflow: hidden;
          }
        }
      `}</style>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        height: '100%'
      }}>
      <div className={`header-mobile-row ${!(code && os) ? 'no-app' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0 }}>
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={onMobileMenuToggle}
          className="mobile-menu-btn"
          style={{
            display: 'none',
            fontSize: '18px',
            padding: 0,
            height: 'auto',
            marginLeft: 0,
            color: '#000',
            background: 'transparent',
            boxShadow: 'none',
            flexShrink: 0,
            width: '32px',
            minWidth: '32px'
          }}
        />
        <div className="brand-container" style={{ 
          display: 'flex',
          alignItems: 'center', 
          gap: '8px',
          flexShrink: 0
        }}>
          <img 
            src="/favicon.ico" 
            alt="logo" 
            style={{ 
              width: '32px', 
              height: '32px',
              objectFit: 'contain'
            }} 
          />
          {!collapsed && (
            <span 
              className="header-title"
              style={{ 
                fontSize: '18px',
                fontWeight: 600,
              }}
            >
              {auth?.studio ? auth.studio : 'Global'}
            </span>
          )}
        </div>
        {code && os && (
          <div 
            className={`d-flex align-items-center app-select-container ${collapsed ? 'is-collapsed' : ''}`}
            style={{ '--sidebar-width': `${sidebarWidth}px` }}
          >
            <Select
              className="app-select"
              value={selectedValue}
              onChange={handleChangeApp}
              placeholder="Select app"
              options={data.map(opt => ({
                value: opt.value,
                title: `${opt.label}`,
                label: (
                  <div className="app-select-option" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ position: 'relative', width: 30, height: 30, display: 'inline-block', flexShrink: 0 }}>
                      {opt.thumb ? (
                        <img src={opt.thumb} alt="" style={{ width: 30, height: 30, borderRadius: 6, objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 30, height: 30, borderRadius: 6, background: '#f0f0f0' }} />
                      )}
                      {opt.os ? (
                        <img src={`/bg-${String(opt.os).toLowerCase()}.png`} alt="os" style={{ position: 'absolute', right: 0, bottom: 0, width: 30, height: 30, display: 'block' }} />
                      ) : null}
                    </span>
                    <span className="app-select-label">{opt.label}</span>
                  </div>
                )
              }))}
              dropdownMatchSelectWidth={false}
              style={{ minWidth: 260, maxWidth: 420 }}
              showSearch
              filterOption={(input, option) => (option?.title || '').toLowerCase().includes(input.toLowerCase())}
              size="middle"
            />
          </div>
        )}
        {/* Mobile avatar placed in the same flex row */}
        <div className="mobile-avatar" style={{ display: 'none' }}>
          <Dropdown menu={{ items: dropdownItems }} trigger={["hover"]} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              {renderAvatar(22)}
            </Space>
          </Dropdown>
        </div>
      </div>
      <Space size="middle" className="desktop-avatar" style={{ alignItems: 'center' }}>
        <Dropdown menu={{ items: dropdownItems }} trigger={["hover"]} placement="bottomRight">
          <Space style={{ cursor: 'pointer' }}>
            {renderAvatar(22)}
            <Text className="user-name">{auth?.fullname || auth?.name || ''}</Text>
          </Space>
        </Dropdown>
      </Space>
    </div>
    </>
  )
}

export default Header;